import type { ZodType } from 'zod';
import type { FormErrors } from '@mantine/form';

/**
 * zod → @mantine/form validation bridge.
 *
 * `mantine-form-zod-resolver@1.3.0` declares `zod >= 3.25` but reads `error.errors`, a
 * property zod 4 renamed to `issues`. The result is not a crash at import time — it is a
 * form that blocks submission and shows no message, which is the worst possible failure
 * mode for a login screen. Twenty lines here remove the dependency and the ambiguity.
 *
 * Paths are joined with dots so nested and array fields (`bonuses.0.value`) address the
 * same way @mantine/form's `getInputProps` does.
 */
export function zodResolver<T>(schema: ZodType<T>) {
  return (values: unknown): FormErrors => {
    const result = schema.safeParse(values);
    if (result.success) return {};

    const errors: FormErrors = {};
    for (const issue of result.error.issues) {
      const key = issue.path
        .map((segment) => (typeof segment === 'symbol' ? String(segment) : segment))
        .join('.');
      // First message per field wins: showing three complaints about one input is noise.
      if (!(key in errors)) errors[key] = issue.message;
    }
    return errors;
  };
}
